import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { FC, Fragment, useEffect, useRef } from 'react';
import Spinner from '../svgs/Spinner';
import Button, { ButtonProps } from './Button';
import {
  AnalyticsComponent,
  AnalyticsPage,
  trackProviderComponentViewedEvent,
} from '../../lib/analytics';
import IconButton from './IconButton';
import ModalCloseX from '../svgs/ModalCloseX';
import useOnKeyPress from '../hooks/useOnKeyPress';

export type ModalDialogProps = {
  title?: string; // Used for analytics tracking
  originPage?: AnalyticsPage | string; // Used for analytics tracking
  children: React.ReactNode;
  isOpen: boolean;
  setClosed: () => void;
  fetching?: boolean;
  width?: 'xs' | 'small' | 'medium' | 'large';
  overflow?: 'hidden' | 'auto';
  className?: string;
  leftFooter?: React.ReactNode;
  rightFooter?: React.ReactNode;
  hideFooter?: boolean;
  primaryActionTitle?: string;
  primaryActionOnClick?: () => void;
  primaryActionDisabled?: boolean;
  primaryActionProps?: ButtonProps;
  primaryActionOnEnter?: boolean;
  bodyClassName?: string;
  titleClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  wrapperClassName?: string;
  /** When true, body gets flex-1 + overflow-y-auto. Pair with overflow="auto" for correct behavior. */
  fullHeight?: boolean;
  isBlocking?: boolean;
};

const ModalDialog: FC<ModalDialogProps> = ({
  title,
  originPage,
  children,
  isOpen,
  setClosed,
  fetching = false,
  width = 'large',
  overflow = 'hidden',
  className,
  leftFooter,
  rightFooter,
  hideFooter = false,
  primaryActionTitle,
  primaryActionOnClick,
  primaryActionDisabled = false,
  primaryActionProps,
  primaryActionOnEnter = false,
  bodyClassName,
  titleClassName,
  headerClassName,
  footerClassName,
  wrapperClassName,
  fullHeight = false,
  isBlocking = false,
}) => {
  // ModalDialog analytics tracking. Needs 'name' prop to be passed in.
  useEffect(() => {
    if (title && isOpen) {
      trackProviderComponentViewedEvent(AnalyticsComponent.Modal, originPage, {
        modalName: title,
      });
    }
  }, [isOpen]);

  /* https://headlessui.dev/react/dialog#managing-focus-within-your-dialog */
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const modalContentRef = useRef(null);

  useOnKeyPress(
    'Enter',
    (event: KeyboardEvent) => {
      if (!primaryActionOnClick || !primaryActionOnEnter) return;
      const target = event.target as HTMLElement;
      const tagName = target?.tagName?.toUpperCase();
      if (tagName === 'INPUT') return;
      if (
        tagName === 'SELECT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'BUTTON'
      )
        return;
      if (target.closest('[role="listbox"]')) return;
      primaryActionOnClick();
    },
    modalContentRef,
    isOpen,
  );

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        initialFocus={closeButtonRef}
        className="fixed inset-0 z-[51] overflow-hidden" // z-index +1 above Slideover; overflow-hidden prevents body scroll from moving the modal
        onClose={isBlocking ? () => void 0 : setClosed}
      >
        <div
          className={classNames(
            'flex px-4 text-center sm:px-0',
            fullHeight
              ? 'h-screen flex-col items-center py-2.5'
              : 'min-h-screen items-center justify-center py-12',
            wrapperClassName,
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-125 bg-opacity-50 backdrop-blur-[4px] transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modaldialog contents. */}
          {!fullHeight && (
            <span
              className="hidden sm:inline-block sm:h-screen sm:align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
          )}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              ref={modalContentRef}
              className={classNames(
                'relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:max-w-[1100px]',
                fullHeight
                  ? 'flex min-h-0 flex-1 flex-col'
                  : 'inline-block align-bottom sm:align-middle',
                {
                  'w-[90%]': width === 'large',
                  'w-[800px]': width === 'medium',
                  'w-[600px]': width === 'small',
                  'w-[435px]': width === 'xs',
                  'overflow-hidden': overflow === 'hidden' || fullHeight,
                  'flex max-h-[85vh] flex-col':
                    overflow === 'auto' && !fullHeight,
                },
                className,
              )}
            >
              {/* Header */}
              <div
                className={classNames(
                  'flex w-full shrink-0 flex-row items-center justify-between border-b border-b-neutral-75 py-3 px-6',
                  headerClassName,
                )}
              >
                <div
                  className={classNames(
                    'font-medium text-green-150',
                    titleClassName,
                  )}
                >
                  {title}
                </div>
                <IconButton
                  aria-label="Close"
                  IconComponent={ModalCloseX}
                  iconClassName="h-6 w-6 min-w-[24px] min-h-[24px]"
                  onClick={setClosed}
                />
              </div>

              {/* Body */}
              <div
                className={classNames(
                  'p-6',
                  overflow === 'auto' &&
                    !fullHeight &&
                    'min-h-0 overflow-y-auto',
                  fullHeight && 'min-h-0 flex-1 overflow-y-auto',
                  bodyClassName,
                )}
              >
                {children}
              </div>

              {/* Footer */}
              {!hideFooter && (
                <div
                  className={classNames(
                    'flex w-full shrink-0 flex-row items-center border-t border-t-neutral-75 py-5 px-6',
                    leftFooter ? 'justify-between' : 'justify-end',
                    footerClassName,
                  )}
                >
                  {leftFooter && leftFooter}
                  <div>
                    {primaryActionTitle && (
                      <Button
                        onClick={primaryActionOnClick}
                        title={primaryActionTitle}
                        disabled={primaryActionDisabled}
                        {...primaryActionProps}
                      />
                    )}
                    {rightFooter && rightFooter}
                  </div>
                </div>
              )}

              {/* Loading spinner */}
              <Transition
                show={fetching}
                as={Fragment}
                enter="transform ease-out duration-100 transition"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transform ease-in duration-100 transition"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute inset-0 z-50 flex h-full w-full items-center justify-center bg-neutral-25/80">
                  <Spinner className="h-6 w-6 text-green-150" />
                </div>
              </Transition>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalDialog;
